import React, { useEffect, useState } from "react";
import { FaCheck, FaUser } from "react-icons/fa";
import {
  FormField,
  SoftButton,
  ToggleSwitch,
} from "../../../components/UI/DesignStandards";
import { SidePotSelection } from "../../../components/createTeam/components/SidePotSelection";
import BowlerBracketTickets from "../../../components/Brackets/BowlerBracketTickets";
import { isProductShuffledOnSquad } from "../../../utility/brackets/entryTickets";

const addOptionalFieldsLabel = (names) => {
  if (names.length === 1) return `Add ${names[0]}`;
  if (names.length === 2) return `Add ${names[0]} or ${names[1]}`;
  if (names.length === 3) return `Add ${names[0]}, ${names[1]}, or ${names[2]}`;
  return "";
};

const BowlerCard = ({
  bowler,
  index,
  tournamentEvents,
  onInputChange,
  onPaidEventChange,
  bracketProducts = [],
  onTicketChange,
  squadId,
  allEventConfigs = [],
  allEventStatus = null,
  selectedEvent = null,
  getAllEventStatus = null,
  currentTeamId = null, // Team ID for edit mode
  onChangeBowler,
  changeBowlerBlockReason = null,
  averageError = "",
}) => {
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  const hasUsbc = Boolean(String(bowler.usbcID || "").trim());
  const hasEmail = Boolean(String(bowler.email || "").trim());
  const hasPhone = Boolean(String(bowler.phone || "").trim());
  const showUsbcField = hasUsbc || showOptionalDetails;
  const showEmailField = hasEmail || showOptionalDetails;
  const showPhoneField = hasPhone || showOptionalDetails;
  const hiddenOptionalNames = [
    !hasUsbc ? "USBC ID" : null,
    !hasEmail ? "Email" : null,
    !hasPhone ? "Phone" : null,
  ].filter(Boolean);

  useEffect(() => {
    setShowOptionalDetails(false);
  }, [bowler.id]);

  return (
    <div className="card-inset p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2 min-w-0">
          <FaUser className="text-brand-muted flex-shrink-0" />
          <span className="font-display font-bold text-brand-charcoal break-words">
            {bowler.name}
          </span>
        </div>
        <SoftButton
          type="button"
          size="xs"
          disabled={Boolean(changeBowlerBlockReason)}
          onClick={() => onChangeBowler(index)}
          className="flex-shrink-0"
        >
          Change bowler
        </SoftButton>
      </div>
      {changeBowlerBlockReason ? (
        <p className="text-xs text-brand-muted">{changeBowlerBlockReason}</p>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-brand-charcoal">
            Average
          </label>
          <div className="flex items-center gap-2">
            <ToggleSwitch
              checked={bowler.hasAverage !== false}
              onChange={(checked) =>
                onInputChange(index, "hasAverage", checked)
              }
              disabled={bowler.isVerified}
              size="sm"
            />
            <span className="text-sm text-brand-muted">Has average</span>
          </div>
        </div>

        {bowler.hasAverage !== false ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={bowler.average || ""}
              onChange={(e) => {
                const value = e.target.value;
                const intValue =
                  value === "" ? "" : Math.round(parseFloat(value) || 0);
                onInputChange(index, "average", intValue);
              }}
              onWheel={(e) => {
                e.target.blur();
              }}
              disabled={bowler.isVerified}
              className={`w-full px-4 py-3 border rounded-lg text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink ${
                averageError ? "border-red-300" : "border-brand-charcoal/20"
              } ${
                bowler.isVerified
                  ? "bg-brand-cream cursor-not-allowed"
                  : "bg-white"
              }`}
              placeholder="Enter average"
              min="0"
              max="300"
              step="1"
            />
            {bowler.isVerified && (
              <span
                className="shrink-0 inline-flex items-center gap-1 bg-brand-charcoal text-brand-cream px-2 py-1 rounded-lg text-xs font-display font-bold"
                title="Verified"
              >
                <FaCheck className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
        ) : (
          <div className="px-3 py-2 card-inset text-sm text-brand-muted">
            No average. Will bowl scratch.
          </div>
        )}
        {averageError ? (
          <p className="text-sm text-red-600">{averageError}</p>
        ) : null}
      </div>

      {(showUsbcField || showEmailField) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showUsbcField ? (
            <FormField
              label="USBC ID"
              value={bowler.usbcID}
              onChange={(e) => onInputChange(index, "usbcID", e.target.value)}
              placeholder="Enter USBC ID"
            />
          ) : null}
          {showEmailField ? (
            <FormField
              label="Email"
              type="email"
              value={bowler.email}
              onChange={(e) => onInputChange(index, "email", e.target.value)}
              placeholder="Enter email address"
            />
          ) : null}
        </div>
      )}

      {showPhoneField ? (
        <FormField
          label="Phone"
          type="tel"
          value={bowler.phone}
          onChange={(e) => onInputChange(index, "phone", e.target.value)}
          placeholder="Enter phone number"
        />
      ) : null}

      {!showOptionalDetails && hiddenOptionalNames.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowOptionalDetails(true)}
          className="inline-flex items-center min-h-11 text-sm font-medium text-brand-gold-ink hover:underline"
        >
          {addOptionalFieldsLabel(hiddenOptionalNames)}
        </button>
      ) : null}

      {tournamentEvents.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-brand-charcoal">
            Side Pots
          </label>
          <SidePotSelection
            sidePotEvents={tournamentEvents}
            selectedSidePots={bowler.selectedSidePots || {}}
            onToggle={(eventId) => onPaidEventChange(index, eventId)}
            disabled={false}
            allEventConfigs={allEventConfigs || []}
            allEventStatus={allEventStatus}
            selectedEvent={selectedEvent}
            getAllEventStatus={getAllEventStatus}
            currentTeamId={currentTeamId}
          />
        </div>
      )}

      <BowlerBracketTickets
        products={bracketProducts}
        counts={bowler.bracketTickets || {}}
        onChange={(productId, nextCount) =>
          onTicketChange && onTicketChange(index, productId, nextCount)
        }
        isShuffled={(product) => isProductShuffledOnSquad(product, squadId)}
      />
    </div>
  );
};

export default BowlerCard;
